import { LitElement, html, css, unsafeCSS } from 'lit';
import leafletCss from 'leaflet/dist/leaflet.css?inline';
import L from 'leaflet';
import { lucideIcon } from '../icon.js';
import { confirmDialog } from '../utils/confirm-dialog.js';
import { renderTekeningenPaneel, TEKENINGEN_PANEEL_CSS } from '../utils/tekeningen-paneel.js';
import { boringIcon, renderKaartLegenda, KAART_LEGENDA_CSS, legendaIngeklapt, bewaarLegendaIngeklapt } from '../utils/kaart-symbolen.js';
import { tipMove, tipHide } from '../utils/hover-tip.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { Search, Layers, TestTube, MapPin, Copy, Check, Pentagon, Upload, Eye, EyeOff, Trash2, Image as ImageIcon } from 'lucide';
import { convertFromWgs84, COORDINATE_SYSTEMS, PLANNED_POINT_KINDS } from '@mv/models';
import {
  SUBLOCATIONS_EVENT, SUBLOCATION_MAP_CSS, PolygonDrawer, drawSublocations, inSublocation,
  sublocationBounds, nextSublocationName, loadSublocations, saveNewSublocation,
} from '../utils/sublocations.js';
import { api } from '../api.js';
import './sublocation-editor.js';
import { RotatedImageOverlay, vectorLayer, plannedPointIcon, PLANNED_KIND_COLORS, layerOpacity, opacityPatch, applyOpacity } from '../utils/kaart-import/leaflet-layers.js';

/** Zelfde deellocaties, in welke volgorde ook? */
const sameIds = (a, b) => {
  const x = [...(a || [])].sort(), y = [...(b || [])].sort();
  return x.length === y.length && x.every((v, i) => v === y[i]);
};

const esc = (s) => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

// Tegels:
//  • Wereldwijde basis (kaart + satelliet) = MapTiler — commercieel toegestaan met API-key.
//    Key via env-var (client-side; beperk de key op domein in het MapTiler-dashboard).
//  • PDOK/Kadaster (BRT + luchtfoto) blijft erbovenop voor scherp NL-detail (CC-BY, gratis,
//    commercieel toegestaan). CARTO en Esri zijn eruit — die stonden commercieel niet toe.
const MAPTILER_KEY = import.meta.env.VITE_MAPTILER_KEY || '';
// 256px-endpoint zodat het qua tegelraster/zoom gelijk loopt met PDOK (geen zoomOffset nodig).
const TILE_WORLD = `https://api.maptiler.com/maps/streets-v2/256/{z}/{x}/{y}.png?key=${MAPTILER_KEY}`;
const TILE_WORLD_AERIAL = `https://api.maptiler.com/maps/satellite/256/{z}/{x}/{y}.jpg?key=${MAPTILER_KEY}`;
const TILE_BRT = 'https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0/standaard/EPSG:3857/{z}/{x}/{y}.png';
const PDOK_ATTR = '&copy; <a href="https://www.pdok.nl" target="_blank" rel="noopener">PDOK</a> / Kadaster';
const MT_ATTR = '&copy; <a href="https://www.maptiler.com/" target="_blank" rel="noopener">MapTiler</a> &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors';
const MT_SAT_ATTR = '&copy; <a href="https://www.maptiler.com/" target="_blank" rel="noopener">MapTiler</a>';

/**
 * Projectkaart: alle boringen op een kaart + verticale lijst. Klik op een marker
 * highlight de boring; klik in de lijst 'vliegt' naar de boring. Bij openen zoomt
 * de kaart zo dat alle gelokaliseerde boringen passen. Popup toont RD + hoogte.
 *
 * Deellocaties: met 'Deellocatie' teken je een vlak op de kaart en geef je het een naam; het
 * wordt bij het project opgeslagen. Klik op een vlak om het te hernoemen of te verwijderen.
 * Welke boringen erin liggen volgt uit hun coördinaten (zie utils/sublocations.js).
 *
 * @fires open-boring  detail:{ guid } — gebruiker klikt "Open boring →" in de popup
 * @fires open-import  — gebruiker wil een meetstaat of tekening op de kaart zetten
 * @fires planned-point-update  detail:{ id, data } — gepland punt verplaatst
 * @fires planned-point-delete  detail:{ id }
 * @fires map-layer-update  detail:{ id, data } — tekening aan/uit
 * @fires map-layer-delete  detail:{ id }
 *
 * Geplande punten (uit een meetstaat of tekening) staan als open ring met naam; is er een
 * boring met dezelfde naam, dan wordt de ring groen met een vinkje. Tekeningen liggen onder
 * de punten, in een eigen pane.
 */
export class ProjectMap extends LitElement {
  static properties = {
    _layersOpen: { state: true },   // paneel 'Tekeningen' op de kaart open
    plannerAvailable: { type: Boolean }, // Vooronderzoek beschikbaar: geplande punten daar verplaatsen
    _showPlanned: { state: true },  // geplande boringen tonen op deze kaart
    _showDone: { state: true },     // ook de uitgevoerde geplande boringen tonen (standaard niet)
    _legendaDicht: { state: true }, // legenda ingeklapt
    borings: { type: Array },   // [{ guid, name, depth, lagen, peilbuizen, lat, lng, rdX, rdY, heightLabel, status }]
    _selected: { type: String, state: true },
    _base: { type: String, state: true },     // 'map' | 'aerial'
    _query: { type: String, state: true },
    _placingGuid: { type: String, state: true }, // boring waarvoor we een locatie op de kaart plaatsen
    // Compacte weergave voor de overzichtstegel: alleen de kaart, zonder lijst, zoekveld,
    // laagkeuze of teller. Doel is 'in één oogopslag zien waar de boringen liggen'; alles
    // waarmee je de kaart BEDIENT hoort op de Kaart-tab thuis.
    compact: { type: Boolean, reflect: true },
    /** Nodig voor de deellocaties; zonder project-id tekent de kaart er geen. */
    projectId: { type: String },
    _sublocations: { state: true },
    _dlMode: { state: true },      // null | 'drawing' (punten zetten) | 'naming' (naam geven)
    _dlPoints: { state: true },    // aantal geplaatste hoekpunten
    _dlName: { state: true },
    _dlError: { state: true },
    _dlBusy: { state: true },
    _dlSelectedId: { state: true },   // deellocatie waarvan het beheerpaneel openstaat
    _dlOverrides: { state: true },    // boring-id → lijst deellocatie-id's, net toegewezen (zie _assign)
    plannedPoints: { type: Array }, // [{ id, name, kind, x(lon), y(lat), plannedDepthCm, status, boringId }]
    mapLayers: { type: Array },     // [{ id, name, kind:'vector'|'image', georef, visible }]
    coordSystem: { type: String },  // projectstelsel, voor de coördinaten van een gepland punt
    _movingPointId: { type: String, state: true },
  };

  static styles = css`
    ${unsafeCSS(leafletCss)}
    /*
     * isolation: isolate — houdt de z-index-ladder van Leaflet BINNEN dit component.
     *
     * Leaflet zet zijn lagen op 400-700 en zijn bediening tot 1000. Zonder eigen stapelcontext
     * doen die getallen mee in de stapeling van de hele pagina, en dan wint een kaartlaag van
     * de topbalk (100) -- inclusief alles wat daarin uitklapt, want een menu komt nooit hoger
     * dan de balk waar het in hangt. Zo verdween het profielmenu achter de kaart.
     *
     * Bewust hier en niet door de balken hoger te zetten: die zouden dan ook boven de
     * volledig-schermvensters (1000) uitkomen die overal in de applicatie staan. Dit perkt het
     * probleem in bij de bron, en isolation doet precies dat ene ding -- een stapelcontext
     * maken -- zonder zelf een z-index in de strijd te gooien.
     *
     * (Geen accenttekens in dit commentaar: het staat binnen een css-sjabloon en zou het
     * sjabloon dan afbreken.)
     */
    :host { display:block; isolation: isolate; }
    /* Compact: de tegel bepaalt de hoogte, en alles wat bediening is verdwijnt. */
    :host([compact]) { height: 100%; }
    :host([compact]) .wrap { height: 100%; border: none; border-radius: 0; }
    :host([compact]) .side,
    :host([compact]) .footer { display: none; }
    /* De laagkeuze blijft: kaart of luchtfoto verandert wát je ziet, en juist bij een snelle
       blik wil je soms de luchtfoto. 'Alles tonen' hoort daar niet bij -- de tegel past zich
       al vanzelf om alle boringen. */
    :host([compact]) .overlay-tr { top: 8px; right: 8px; gap: 0; }
    :host([compact]) .overlay-tr .btn-plain { display: none; }
    :host([compact]) .place-hint { display: none; }
    :host([compact]) .legend { display: none; }
    :host([compact]) .seg button { padding: 4px 9px; font-size: 0.7rem; }
    /* Bronvermelding klein maar aanwezig: OpenStreetMap, MapTiler en PDOK vragen erom in
       hun voorwaarden, dus weglaten mag niet -- ook niet in een miniatuur. Leaflets
       standaardformaat trekt hier te veel aandacht voor iets wat je nooit leest. */
    .leaflet-control-attribution { font-size: 9px; line-height: 1.4; padding: 0 5px; opacity: .6; }
    .leaflet-control-attribution a { text-decoration: none; }
    .leaflet-control-attribution:hover { opacity: 1; }
    :host([compact]) .leaflet-control-attribution { font-size: 8px; padding: 0 3px; opacity: .5; }
    :host([compact]) .leaflet-control-scale { display: none; }
    .wrap { display:flex; gap:0; height:560px; border:1px solid var(--mv-border); border-radius:10px; overflow:hidden; background:var(--mv-surface); }
    .side { width:240px; flex-shrink:0; border-right:1px solid var(--mv-border); display:flex; flex-direction:column; background:var(--mv-surface); }
    .search { padding:10px; border-bottom:1px solid var(--mv-border); }
    .search-box { display:flex; align-items:center; gap:6px; background:var(--mv-surface-alt,#f3f4f6); border:1px solid var(--mv-border); border-radius:7px; padding:6px 8px; }
    .search-box input { border:none; background:none; outline:none; font-size:0.82rem; color:var(--mv-text); width:100%; font-family:inherit; }
    .list { flex:1; overflow-y:auto; padding:6px; }
    .sec-label { font-size:0.62rem; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; color:var(--mv-text-muted); padding:8px 8px 4px; }
    .item { display:flex; align-items:center; gap:9px; padding:7px 9px; border-radius:7px; cursor:pointer; }
    .item:hover { background:var(--mv-surface-alt,#f3f4f6); }
    .item.active { background:var(--mv-primary-tint,#e9f5e6); }
    .item.active .item-name { color:var(--mv-primary); }
    .item.nogps { cursor:default; opacity:0.85; }
    .item.nogps:hover { background:none; }
    .item.nogps.placing { opacity:1; background:var(--mv-primary-tint, rgba(46,158,91,0.08)); }
    .add-coord { display:inline-block; font-size:0.72rem; font-weight:600; color:var(--mv-primary); cursor:pointer; }
    .add-coord:hover { text-decoration:underline; }
    .item-ico { flex-shrink:0; color:var(--mv-text-muted); display:inline-flex; }
    .item.active .item-ico { color:var(--mv-primary); }
    .item-text { min-width:0; }
    .item-name { font-size:0.84rem; font-weight:600; color:var(--mv-text); }
    .item-sub { font-size:0.72rem; color:var(--mv-text-muted); }
    .item-ico.planned { width:11px; height:11px; margin:0 2px; border-radius:50%; border:2.5px solid #ea580c; box-sizing:border-box; }
    .item-ico.planned.peilbuis { border-color:#0284c7; }
    .item-ico.planned.proefgat, .item-ico.planned.sleuf { border-color:#a16207; border-radius:3px; }
    .layer-item { cursor:default; }
    .tekeningmenu { position:relative; }
    .telling { font-size:.68rem; font-weight:700; background:var(--mv-primary-tint,#e9f5e6); color:var(--mv-primary); border-radius:999px; padding:0 7px; line-height:1.5; }
    ${unsafeCSS(TEKENINGEN_PANEEL_CSS)}
    .tekeningpaneel .uploaden { width:100%; justify-content:center; margin-bottom:4px; box-shadow:none; }
    .btn-plain.on { border-color:var(--mv-primary); color:var(--mv-primary); }
    .tekeningpaneel { position:absolute; right:0; top:calc(100% + 6px); width:280px; max-height:320px; overflow:auto; padding:6px;
                      background:var(--mv-surface); border:1px solid var(--mv-border); border-radius:10px; box-shadow:0 6px 18px rgba(0,0,0,.18); }
    .dekking { width:100%; max-width:140px; margin-top:4px; accent-color:var(--mv-primary); display:block; }
    .layer-item .item-text { flex:1; cursor:pointer; }
    .item-name.off { color:var(--mv-text-muted); font-weight:500; }
    .lbtn { border:none; background:none; padding:2px; cursor:pointer; color:var(--mv-text-muted); display:inline-flex; }
    .lbtn:hover { color:var(--mv-text); }
    /* Altijd zichtbaar (gedempt): alleen bij hover was hij niet te vinden. */
    .lbtn.del { opacity:.55; }
    .layer-item:hover .lbtn.del { opacity:1; }
    .lbtn.del:hover { color:#b91c1c; }
    .map-area { flex:1; position:relative; min-width:0; }
    #map { position:absolute; inset:0; background:#e9e7e0; }
    #map.placing { cursor:crosshair; }
    /* Ook boven een deellocatie: tijdens het verplaatsen is een klik daar de nieuwe plek. */
    #map.placing .leaflet-interactive { cursor:crosshair; }
    .place-hint { position:absolute; top:10px; left:50%; transform:translateX(-50%); z-index:600; display:flex; align-items:center; gap:9px; background:var(--mv-surface); border:1px solid var(--mv-primary); border-radius:8px; padding:8px 12px; font-size:0.82rem; color:var(--mv-text); box-shadow:0 2px 8px rgba(0,0,0,0.18); max-width:90%; }
    .place-hint button { border:none; background:var(--mv-bg,#f1f3f5); color:var(--mv-text); border-radius:6px; padding:4px 10px; font-size:0.76rem; font-weight:600; cursor:pointer; }
    .overlay-tr { position:absolute; top:10px; right:10px; z-index:500; display:flex; gap:8px; }
    .seg { display:flex; background:var(--mv-surface); border:1px solid var(--mv-border); border-radius:7px; overflow:hidden; box-shadow:0 1px 4px rgba(0,0,0,0.12); }
    .seg button { border:none; background:none; padding:6px 12px; font-size:0.76rem; font-weight:600; color:var(--mv-text-muted); cursor:pointer; font-family:inherit; }
    .seg button.on { background:var(--mv-primary); color:#fff; }
    .btn-plain { display:inline-flex; align-items:center; gap:5px; background:var(--mv-surface); border:1px solid var(--mv-border); border-radius:7px; padding:6px 12px; font-size:0.76rem; font-weight:600; color:var(--mv-text); cursor:pointer; box-shadow:0 1px 4px rgba(0,0,0,0.12); font-family:inherit; }
    .footer { position:absolute; left:50%; transform:translateX(-50%); bottom:8px; z-index:500; background:var(--mv-surface); border:1px solid var(--mv-border); border-radius:6px; padding:4px 9px; font-size:0.72rem; color:var(--mv-text-muted); box-shadow:0 1px 4px rgba(0,0,0,0.12); white-space:nowrap; }
    /* pointer-events:none — anders vangt deze overlay (inset:0) alle muis-events af,
       waardoor je de kaart niet kunt verslepen of erop kunt klikken zolang er nog
       geen gelokaliseerde boringen zijn. De tekst hoeft niet klikbaar te zijn. */
    .empty { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; text-align:center; color:var(--mv-text-muted); font-size:0.85rem; padding:1rem; pointer-events:none; }
    .leaflet-container { font: inherit; }
    /* Legenda linksonder, boven de schaalbalk van Leaflet (gedeeld: utils/kaart-symbolen.js). */
    ${unsafeCSS(KAART_LEGENDA_CSS)}
    .legend { bottom:38px; }
    ${unsafeCSS(SUBLOCATION_MAP_CSS)}
    .dl-bar { border-color:#c2410c; flex-wrap:wrap; }
    .dl-bar input { border:1px solid var(--mv-border); border-radius:6px; padding:4px 8px; font:inherit; font-size:0.82rem; width:9rem; color:var(--mv-text); background:var(--mv-surface); }
    .dl-bar button.primary { background:#c2410c; color:#fff; }
    .dl-bar button:disabled { opacity:.5; cursor:default; }
    .dl-error { color:var(--mv-danger,#c0392b); font-size:0.76rem; flex-basis:100%; }
    .item-dl { color:#c2410c; font-weight:600; }
    /* Een deellocatie bewerken: een kaart rechts over de kaart, niet weggestopt in de zijbalk. */
    .item.dl-active { background:#fff7f2; }
    .item.dl-active .item-name { color:#c2410c; }
    .dl-panel { position:absolute; top:58px; right:12px; bottom:12px; width:min(420px, calc(100% - 80px)); z-index:650; }
    .mv-map-popup .leaflet-popup-content-wrapper { border-radius: 11px; box-shadow: 0 4px 20px rgba(0,0,0,0.18); }
    .mv-map-popup .leaflet-popup-content { margin: 12px 14px 12px 14px; }
    .leaflet-tooltip.mv-map-tip { background:#1f2933; color:#fff; border:none; border-radius:5px; padding:2px 8px; font:600 11px/1.3 system-ui,-apple-system,sans-serif; box-shadow:0 1px 5px rgba(0,0,0,0.3); }
    .leaflet-tooltip.mv-map-tip::before { border-top-color:#1f2933; }
  `;

  constructor() {
    super();
    this.borings = [];
    this._selected = null;
    this._base = 'map';
    this._query = '';
    this._placingGuid = null;
    this._placeMarker = null;
    this._onMapClickPlace = null;
    this._map = null;
    this._markers = new Map();
    this._byGuid = new Map();
    this.plannedPoints = [];
    // Of de geplande boringen getoond worden; per browser onthouden.
    try { this._showPlanned = localStorage.getItem('mv-kaart-gepland') !== 'uit'; } catch (_) { this._showPlanned = true; }
    // Uitgevoerde geplande boringen: standaard verborgen (de boring zelf staat al op de kaart),
    // net als in de veldapp. Per browser onthouden.
    try { this._showDone = localStorage.getItem('mv-kaart-gepland-uitgevoerd') === 'aan'; } catch (_) { this._showDone = false; }
    // Klik buiten het paneel 'Tekeningen' -- ook in de kaart -- sluit het.
    this._onPointerDown = (e) => {
      if (!this._layersOpen) return;
      const menu = this.renderRoot?.querySelector('.tekeningmenu');
      if (menu && !e.composedPath().includes(menu)) this._layersOpen = false;
    };
    this.mapLayers = [];
    this.coordSystem = 'RD';
    this._movingPointId = null;
    this._plannedMarkers = new Map();
    this._layerObjs = new Map(); // id → Leaflet-laag; één keer opgehaald, daarna hergebruikt
    this._layerLoading = new Set();
    this._sublocations = [];
    this._dlMode = null;
    this._dlPoints = 0;
    this._dlName = '';
    this._dlError = '';
    this._dlBusy = false;
    this._dlRing = null;
    this._dlSelectedId = null;
    this._dlOverrides = {};
    this._onSublocations = (e) => {
      if (e.detail?.projectId === this.projectId) this._sublocations = e.detail.sublocations;
    };
    // Escape breekt het tekenen af, net als 'Annuleren'.
    this._onKey = (e) => { if (e.key === 'Escape' && this._dlMode) this._cancelDrawing(); };
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener(SUBLOCATIONS_EVENT, this._onSublocations);
    window.addEventListener('keydown', this._onKey);
    document.addEventListener('pointerdown', this._onPointerDown, true);
  }

  firstUpdated() {
    this._initMap();
    this._sig = this._borSig();
    // In een tegel wordt de kaart pas na de eerste render op zijn echte formaat gezet.
    // Leaflet rekent de uitsnede met de maat die het bij initialisatie zag, dus zonder deze
    // hermeting past hij de boringen in een kaart die veel hoger is dan wat je ziet -- en
    // valt de helft buiten beeld.
    if (this._map) {
      this._resizeObserver = new ResizeObserver(() => {
        this._map?.invalidateSize();
        // Alleen de tegel past zichzelf opnieuw in. Op de volledige kaart zou een
        // venster-resize het pannen en zoomen van de gebruiker ongedaan maken.
        if (this.compact && !this._selected) this._fitAll();
      });
      this._resizeObserver.observe(this.renderRoot.querySelector('#map'));
    }
  }

  updated(changed) {
    if (changed.has('projectId')) this._loadSublocations();
    // Wat de herladen boringen al bevestigen, hoeft niet meer als override te blijven staan.
    if (changed.has('borings') && Object.keys(this._dlOverrides).length) {
      const byId = new Map((this.borings || []).map((b) => [b.guid, b]));
      const left = Object.fromEntries(Object.entries(this._dlOverrides)
        .filter(([id, v]) => byId.has(id) && !sameIds(byId.get(id).sublocationIds, v)));
      if (Object.keys(left).length !== Object.keys(this._dlOverrides).length) this._dlOverrides = left;
    }
    // Een open popup van een boring meteen bijwerken als zijn deellocaties veranderen (Leaflet
    // roept de inhoudsfunctie opnieuw aan bij update()).
    if (changed.has('_dlOverrides') || changed.has('borings') || changed.has('_sublocations')) {
      const open = this._selected && this._markers.get(this._selected)?.getPopup();
      if (open?.isOpen()) open.update();
    }
    // Een verwijderde deellocatie heeft geen paneel meer.
    if (changed.has('_sublocations') && this._dlSelectedId && !this._dlSelected) this._dlSelectedId = null;
    if (changed.has('_sublocations') && this._dlLayer) this._drawSublocations();
    // De ouder geeft elke render een nieuwe borings-array door; alleen echt bijwerken
    // als de punten (guid + coördinaten) veranderd zijn — anders sluit een parent-render
    // (bv. WS-update) de open popup en herpositioneert de kaart onnodig.
    if (changed.has('borings') && this._map) {
      const sig = this._borSig();
      if (sig !== this._sig) {
        this._sig = sig;
        this._rebuildMarkers();
        if (!this._selected) this._fitAll(); // alleen auto-fit zolang niets geselecteerd is
      }
    }
    if (changed.has('plannedPoints') && this._map) {
      const sig = this._plannedSig();
      if (sig !== this._pSig) {
        // Alleen de eerste keer inpassen (punten net geladen of net geïmporteerd); daarna
        // zou elke WS-update de uitsnede van de gebruiker weggooien.
        const first = !this._pSig;
        this._pSig = sig;
        this._rebuildPlanned();
        if (first && !this._selected) this._fitAll();
      }
    }
    if (changed.has('mapLayers') && this._map) this._syncLayers();
  }

  _plannedSig() {
    return (this.plannedPoints || []).map(p => `${p.id}:${p.x},${p.y}:${p.status}:${p.boringId}:${p.name}:${p.kind}`).join('|');
  }

  _borSig() { return (this.borings || []).map(b => `${b.guid}:${b.lat},${b.lng}`).join('|'); }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(SUBLOCATIONS_EVENT, this._onSublocations);
    window.removeEventListener('keydown', this._onKey);
    document.removeEventListener('pointerdown', this._onPointerDown, true);
    this._drawer?.cancel();
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    if (this._map) { this._map.remove(); this._map = null; }
  }

  get _located() { return (this.borings || []).filter(b => b.lat != null && b.lng != null); }
  get _noGps()   { return (this.borings || []).filter(b => b.lat == null || b.lng == null); }

  _initMap() {
    const el = this.renderRoot.querySelector('#map');
    if (!el || this._map) return;
    this._map = L.map(el, { zoomControl: true, attributionControl: true, maxZoom: 21 });
    this._map.setView([52.1, 5.3], 8); // NL-fallback tot fitBounds
    // Aparte panes met vaste stapelvolgorde: de wereldwijde onderlaag en de PDOK-detaillaag
    // krijgen elk hun eigen pane. Zonder dit deelden ze één pane en werden de onderliggende
    // tiles bij uitzoomen weggeprunet (laag flikkerde en verdween). Onder markerPane (600).
    this._map.createPane('mvWorld'); this._map.getPane('mvWorld').style.zIndex = 200;
    this._map.createPane('mvDetail'); this._map.getPane('mvDetail').style.zIndex = 300;
    // Kaart-modus = wereldwijde MapTiler-basiskaart + PDOK-BRT bovenop (PNG, buiten NL
    // transparant → geen witte rand). PDOK-BRT begrensd tot NL zodat we buiten NL geen tegels
    // ophalen. Luchtfoto-modus = alléén de wereldwijde MapTiler-satelliet (ook binnen NL scherp
    // genoeg). PDOK-luchtfoto is bewust weggelaten: die is JPEG (opaak wit buiten NL-data), en
    // de rechthoek-bbox dekt ook Noord-België/Duitsland → daar overschreef 'ie de satelliet met
    // wit. Een rechthoek die wél heel NL maar niet België pakt kan niet (Zuid-Limburg = zelfde
    // breedtegraad als België).
    const NL_BOUNDS = L.latLngBounds([[50.5, 3.0], [53.8, 7.5]]);
    if (!MAPTILER_KEY) console.warn('[project-map] VITE_MAPTILER_KEY ontbreekt — de wereldwijde MapTiler-lagen laden niet.');
    this._tiles = {
      map: [
        L.tileLayer(TILE_WORLD, { pane: 'mvWorld', maxZoom: 21, attribution: MT_ATTR }),
        L.tileLayer(TILE_BRT, { pane: 'mvDetail', bounds: NL_BOUNDS, maxZoom: 21, maxNativeZoom: 19, attribution: PDOK_ATTR }),
      ],
      aerial: [
        L.tileLayer(TILE_WORLD_AERIAL, { pane: 'mvWorld', maxZoom: 21, maxNativeZoom: 20, attribution: MT_SAT_ATTR }),
      ],
    };
    this._addBase(this._base);
    this._map.attributionControl.setPrefix(false); // geen 'Leaflet'-vlag/link
    L.control.scale({ imperial: false }).addTo(this._map);
    // Vlakken onder de boringen: overlayPane (400) ligt onder markerPane (600).
    this._dlLayer = L.layerGroup().addTo(this._map);
    this._drawSublocations();
    // Tekeningen onder de boringen en geplande punten, boven de basiskaart.
    this._map.createPane('mvDrawings'); this._map.getPane('mvDrawings').style.zIndex = 350;
    this._plannedLayer = L.layerGroup();
    if (this._showPlanned) this._plannedLayer.addTo(this._map);
    this._markersLayer = L.layerGroup().addTo(this._map);
    this._map.on('click', (e) => this._onMovePointClick(e));
    // Klik op de kaart-achtergrond sluit de popup (Leaflet-default closePopupOnClick) →
    // popupclose ruimt de selectie op. Geen aparte map-click-deselect nodig.
    this._rebuildMarkers();
    this._pSig = this._plannedSig();
    this._rebuildPlanned();
    this._syncLayers();
    this._fitAll();
    // Leaflet meet de containergrootte pas correct nadat het tabblad zichtbaar is.
    setTimeout(() => this._map && this._map.invalidateSize(), 60);
  }

  _icon(b, selected) {
    // Gedeeld met Vooronderzoek: donkerblauw, of blauw met een peilbuis; geselecteerd = groen.
    return boringIcon(b, { selected });
  }


  _rebuildMarkers() {
    this._byGuid = new Map((this.borings || []).map(b => [b.guid, b]));
    this._markers = new Map();
    if (!this._markersLayer) return;
    this._markersLayer.clearLayers();
    for (const b of this._located) {
      const m = L.marker([b.lat, b.lng], { icon: this._icon(b, b.guid === this._selected) });
      // Popup vooraf binden → Leaflet opent 'm zelf bij een marker-klik (werkt betrouwbaar,
      // ook in shadow DOM). Selectie-styling volgt de popupopen/-close-events.
      // Bij openen opbouwen, niet bij het tekenen: de deellocaties kunnen intussen geladen of
      // gewijzigd zijn (toewijzen in het bewerkvenster).
      m.bindPopup(() => this._popupNode(b), { offset: [0, -8], closeButton: true, autoPan: true, minWidth: 220, className: 'mv-map-popup' });
      // Zweeflabel (alleen de code) → een gebied aflopen zonder popups te openen.
      m.bindTooltip(esc(b.name), { direction: 'top', offset: [0, -8], className: 'mv-map-tip', opacity: 1 });
      m.on('popupopen', () => this._onPopupOpen(b.guid));
      m.on('popupclose', () => this._onPopupClose(b.guid));
      m.addTo(this._markersLayer);
      this._markers.set(b.guid, m);
    }
  }

  _fitAll() {
    const pts = [...this._markers.values(), ...(this._showPlanned ? this._plannedMarkers.values() : [])].map(m => m.getLatLng());
    if (!pts.length) { this._map.setView([52.1, 5.3], 8); return; }
    if (pts.length === 1) { this._map.setView(pts[0], 17); return; }
    this._map.fitBounds(L.latLngBounds(pts), { padding: [45, 45], maxZoom: 18 });
  }

  // ── Deellocaties ─────────────────────────────────────────────────────────────
  _loadSublocations() {
    const id = this.projectId;
    if (!id) { this._sublocations = []; return; }
    loadSublocations(id)
      .then((list) => { if (this.projectId === id) this._sublocations = list; })
      .catch((e) => console.warn('[project-map] deellocaties laden mislukt:', e.message));
  }

  _drawSublocations() {
    if (!this._dlLayer) return;
    // De compacte tegel toont de vlakken alleen; bewerken doe je op de Kaart-tab.
    drawSublocations(this._dlLayer, this._sublocations, {
      highlightId: this._dlSelectedId,
      // Kies je net een nieuwe plek voor een boring of een gepland punt, dan is een klik in een
      // vlak die plek, niet 'open deze deellocatie': anders kun je niets binnen een deellocatie zetten.
      onClick: this.compact ? null : (s, e) => {
        if (this._placingGuid) { this._onPlaceClick(e); return; }
        if (this._movingPointId) { this._onMovePointClick(e); return; }
        this._selectSublocation(s, { fit: false });
      },
    });
  }

  /**
   * De deellocaties van een boring (een boring kan in meer liggen): wat de backend teruggaf, of
   * wat we net hebben toegewezen en de ouder nog niet heeft herladen.
   */
  _sublocationIdsOf(b) {
    return Object.prototype.hasOwnProperty.call(this._dlOverrides, b.guid) ? this._dlOverrides[b.guid] : (b.sublocationIds || []);
  }

  /** De deellocaties van een boring met `sublocationId` erbij (`on`) of eraf. */
  _withSublocation(b, sublocationId, on) {
    const ids = this._sublocationIdsOf(b).filter((id) => id !== sublocationId);
    return on ? [...ids, sublocationId] : ids;
  }

  /** Alle boringen van een deellocatie, ook die zonder coördinaat. */
  _boringsIn(s) { return (this.borings || []).filter((b) => this._sublocationIdsOf(b).includes(s.id)); }

  _startDrawing() {
    if (!this._map || !this.projectId) return;
    if (this._placingGuid) this._cancelPlacing();
    this._dlMode = 'drawing';
    this._dlPoints = 0;
    this._dlError = '';
    if (!this._drawer) {
      this._drawer = new PolygonDrawer(this._map, {
        onChange: (n) => { this._dlPoints = n; },
        onFinish: (ring) => this._nameDrawing(ring),
      });
    }
    this._drawer.start();
  }

  /** Vlak staat; nu de naam. Het vlak blijft zichtbaar, zodat je ziet wat je opslaat. */
  async _nameDrawing(ring) {
    this._dlRing = ring;
    this._dlMode = 'naming';
    this._dlName = nextSublocationName(this._sublocations);
    this._dlPreview = L.polygon(ring.map(([lng, lat]) => [lat, lng]), {
      color: '#c2410c', weight: 2, fillColor: '#c2410c', fillOpacity: 0.16, interactive: false,
    }).addTo(this._map);
    await this.updateComplete;
    const input = this.renderRoot.querySelector('.dl-bar input');
    input?.focus();
    input?.select();
  }

  _cancelDrawing() {
    this._drawer?.cancel();
    this._dlPreview?.remove();
    this._dlPreview = null;
    this._dlRing = null;
    this._dlMode = null;
    this._dlError = '';
  }

  async _saveDrawing() {
    const name = this._dlName.trim();
    if (!name) { this._dlError = 'Geef de deellocatie een naam.'; return; }
    this._dlBusy = true;
    this._dlError = '';
    try {
      const created = await saveNewSublocation(this.projectId, { name, geometry: this._dlRing });
      this._cancelDrawing();
      // De boringen in het vlak (of binnen 0,5 m van de rand) horen er meteen bij, ook als ze al
      // bij een andere deellocatie horen: een boring kan in meer deellocaties liggen.
      const inside = this._located.filter((b) => inSublocation(b.lng, b.lat, created));
      if (inside.length) {
        await this._assign(() => api.assignSublocationBorings(created.id, inside.map((b) => b.guid)),
          Object.fromEntries(inside.map((b) => [b.guid, this._withSublocation(b, created.id, true)])));
      }
      this._selectSublocation(created, { fit: false });
    } catch (e) {
      this._dlError = e.message || 'Opslaan mislukt.';
    } finally {
      this._dlBusy = false;
    }
  }

  _fitSublocation(s, { besidePanel = false } = {}) {
    // Met het bewerkvenster open: het vlak links ervan in beeld, niet eronder.
    const right = besidePanel ? Math.min(450, (this._map?.getSize().x || 0) / 2) : 45;
    this._map?.fitBounds(sublocationBounds(s), { paddingTopLeft: [45, 70], paddingBottomRight: [right, 45], maxZoom: 19 });
  }

  // ── Deellocatie beheren (zijbalk) ─────────────────────────────────────────

  get _dlSelected() { return this._sublocations.find((s) => s.id === this._dlSelectedId) || null; }

  /** Een vlak, of een deellocatie in de lijst: het bewerkvenster opent, de kaart gaat erheen. Een boring kiezen sluit het. */
  _selectSublocation(s, { fit = true } = {}) {
    this._dlSelectedId = s.id;
    this._dlError = '';
    this._map?.closePopup();
    if (fit && s.geometry?.length >= 3) this._fitSublocation(s, { besidePanel: true });
    this._drawSublocations();
  }

  _closeSublocation() {
    this._dlSelectedId = null;
    this._dlError = '';
    this._drawSublocations();
  }

  /**
   * Toewijzen: meteen tonen (overrides), dan opslaan, dan de ouder laten herladen. Mislukt het,
   * dan gaan de overrides terug en staat er een melding.
   */
  async _assign(call, overrides) {
    const before = this._dlOverrides;
    this._dlOverrides = { ...before, ...overrides };
    this._dlError = '';
    try {
      await call();
      this.dispatchEvent(new CustomEvent('sublocations-assigned', { bubbles: true, composed: true }));
    } catch (e) {
      this._dlOverrides = before;
      this._dlError = e.message || 'Toewijzen mislukt.';
    }
  }

  // ── Geplande punten ─────────────────────────────────────────────────────────
  _rebuildPlanned() {
    this._plannedMarkers = new Map();
    if (!this._plannedLayer) return;
    this._plannedLayer.clearLayers();
    this._removeDeviationLine();
    for (const p of this.plannedPoints || []) {
      if (p.x == null || p.y == null) continue;
      // Uitgevoerd: standaard niet getoond (de boring zelf staat al op de kaart). Aangezet: een
      // groen rondje met vinkje, en bij aanklikken een stippellijn naar de echte boring.
      if (p.status === 'done' && !this._showDone) continue;
      const m = L.marker([p.y, p.x], { icon: plannedPointIcon(p, { showLabel: !this.compact }), zIndexOffset: -100 });
      m.bindPopup(() => this._plannedPopup(p), { offset: [0, -6], minWidth: 230, className: 'mv-map-popup' });
      if (p.status === 'done') {
        m.on('popupopen', () => this._drawDeviationLine(p));
        m.on('popupclose', () => this._removeDeviationLine());
      }
      m.addTo(this._plannedLayer);
      this._plannedMarkers.set(p.id, m);
    }
  }

  _plannedPopup(p) {
    const wrap = document.createElement('div');
    wrap.style.cssText = 'min-width:200px;font:400 12px/1.4 system-ui,-apple-system,sans-serif;color:#1a1a1a;';
    const sys = COORDINATE_SYSTEMS[this.coordSystem] ? this.coordSystem : 'RD';
    const proj = convertFromWgs84(p.x, p.y, sys);
    const fmt = sys === 'WGS84' ? (n) => n.toFixed(6) : (n) => String(Math.round(n));
    const kind = PLANNED_POINT_KINDS.find(k => k.id === p.kind)?.label || 'Boring';
    const uitvoerder = p.boringId ? this._boringById(p.boringId) : null;
    const afstand = uitvoerder ? this._afstandM(p, uitvoerder) : null;
    const rows = [
      ['Soort', kind],
      ['Diepte', p.plannedDepthCm ? `${p.plannedDepthCm} cm` : '–'],
      ['Status', p.status === 'done' ? '<span style="color:#2e9e5b;font-weight:700">uitgevoerd</span>' : 'nog te doen'],
      uitvoerder ? ['Boring', `${esc(uitvoerder.name)}${afstand != null ? ` <span style="color:#9096a0;font-weight:400;">· ${afstand} m</span>` : ''}`] : null,
      p.note ? ['Opmerking', esc(p.note)] : null,
    ].filter(Boolean).map(([k, v]) => `<div style="color:#8a8f98;">${k}</div><div style="font-weight:600;">${v}</div>`).join('');
    wrap.innerHTML =
      `<div style="display:flex;align-items:baseline;gap:7px;margin-bottom:9px;">
         <span style="font-weight:700;font-size:13px;">${esc(p.name)}</span>
         <span style="color:#9096a0;font-size:11px;">${p.status === 'done' ? 'gepland, uitgevoerd' : 'gepland'}</span>
       </div>
       <div style="display:grid;grid-template-columns:72px 1fr;gap:5px 10px;">${rows}</div>
       ${proj ? `<div style="margin-top:9px;font:600 11px/1.35 ui-monospace,SFMono-Regular,Menlo,monospace;color:#555;">${fmt(proj.x)}<br>${fmt(proj.y)}</div>` : ''}`;
    const actions = document.createElement('div');
    actions.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px 14px;margin-top:10px;padding-top:9px;border-top:1px solid #ececec;';
    const link = (label, icon, onClick, color = '#5b6672') => {
      const a = document.createElement('span');
      a.style.cssText = `display:inline-flex;align-items:center;gap:5px;color:${color};font-weight:600;cursor:pointer;`;
      a.innerHTML = `${lucideIcon(icon, 13)}<span>${label}</span>`;
      a.addEventListener('click', (e) => { e.stopPropagation(); onClick(); });
      actions.appendChild(a);
    };
    if (p.boringId) {
      link('Open boring', MapPin, () => {
        const b = (this.borings || []).find(x => x.id === p.boringId || x.guid === p.boringId);
        if (b) this.dispatchEvent(new CustomEvent('open-boring', { detail: { guid: b.guid }, bubbles: true, composed: true }));
      }, 'var(--mv-primary,#2e9e5b)');
    }
    // Een bestaande boring aanwijzen als de uitvoering van dit punt (zoals 'Koppel bestaande
    // boring' in de veldapp). Bij een uitgevoerd punt: een andere boring kiezen.
    // Ook op de compacte kaart van het dashboard.
    if (this.projectId) {
      link(p.boringId ? 'Andere boring' : 'Vervangen door boring', Check, () => this._toonKoppelKeuze(wrap, p), 'var(--mv-primary,#2e9e5b)');
    }
    link('Verplaatsen', MapPin, () => this._verplaatsGepland(p));
    link('Verwijderen', Trash2, () => {
      this._map.closePopup();
      this.dispatchEvent(new CustomEvent('planned-point-delete', { detail: { id: p.id, name: p.name }, bubbles: true, composed: true }));
    }, '#b91c1c');
    wrap.appendChild(actions);
    return wrap;
  }

  /** De boring met dit (database-)id, zoals de kaart hem kent. */
  _boringById(id) {
    return (this.borings || []).find((b) => b.id === id || b.guid === id) || null;
  }

  /** Afstand in hele meters tussen een gepland punt en een boring (beide WGS84), of null. */
  _afstandM(p, b) {
    if (p?.x == null || p?.y == null || b?.lat == null || b?.lng == null) return null;
    return Math.round(L.latLng(p.y, p.x).distanceTo(L.latLng(b.lat, b.lng)));
  }

  /**
   * Keuze in de popup: welke bestaande boring voert dit punt uit? Dichtstbijzijnde eerst, met de
   * afstand erbij; boringen die al een ander punt uitvoeren staan erbij met dat punt.
   */
  _toonKoppelKeuze(wrap, p) {
    if (wrap.querySelector('.mv-koppel')) return;
    const uitgevoerdDoor = new Map((this.plannedPoints || []).filter((q) => q.boringId && q.id !== p.id).map((q) => [q.boringId, q.name]));
    const opties = (this.borings || []).filter((b) => b.id)
      .map((b) => ({ b, m: this._afstandM(p, b) }))
      .sort((a, z) => (a.m ?? Infinity) - (z.m ?? Infinity) || String(a.b.name).localeCompare(String(z.b.name), 'nl', { numeric: true }));
    const box = document.createElement('div');
    box.className = 'mv-koppel';
    box.style.cssText = 'margin-top:10px;padding-top:9px;border-top:1px solid #ececec;display:flex;flex-direction:column;gap:7px;';
    if (!opties.length) {
      box.innerHTML = '<div style="color:#9096a0;">Dit project heeft nog geen boringen.</div>';
      wrap.appendChild(box);
      return;
    }
    const label = document.createElement('div');
    label.style.cssText = 'font-weight:700;';
    label.textContent = `Welke boring voert ${p.name} uit?`;
    const select = document.createElement('select');
    select.style.cssText = 'width:100%;padding:6px 8px;border:1px solid #d6d9e0;border-radius:7px;font:inherit;background:#fff;color:#1a1a1a;';
    for (const { b, m } of opties) {
      const o = document.createElement('option');
      o.value = b.id;
      o.textContent = [b.name, m != null ? `${m} m` : 'zonder locatie', uitgevoerdDoor.has(b.id) ? `voert ${uitgevoerdDoor.get(b.id)} uit` : null].filter(Boolean).join(' · ');
      if (b.id === p.boringId) o.selected = true;
      select.appendChild(o);
    }
    select.addEventListener('click', (e) => e.stopPropagation());
    const knop = document.createElement('button');
    knop.textContent = 'Koppelen';
    knop.style.cssText = 'background:var(--mv-primary,#2e9e5b);color:#fff;border:none;border-radius:7px;padding:7px 10px;font-weight:700;font-size:12px;cursor:pointer;';
    knop.addEventListener('click', (e) => {
      e.stopPropagation();
      this._map.closePopup();
      this.dispatchEvent(new CustomEvent('planned-point-link', {
        detail: { pointId: p.id, pointName: p.name, boringId: select.value, previousBoringId: p.boringId || null },
        bubbles: true, composed: true,
      }));
    });
    box.append(label, select, knop);
    wrap.appendChild(box);
  }

  /** Stippellijn van een uitgevoerd gepland punt naar de boring die het uitvoert (zoals in de veldapp). */
  _drawDeviationLine(p) {
    this._removeDeviationLine();
    const b = p.boringId ? this._boringById(p.boringId) : null;
    if (!b || b.lat == null || b.lng == null || !this._map) return;
    this._deviationLine = L.polyline([[p.y, p.x], [b.lat, b.lng]], {
      color: '#2e9e5b', weight: 2, dashArray: '5 6', opacity: 0.9, interactive: false,
    }).addTo(this._map);
  }

  _removeDeviationLine() {
    if (this._deviationLine && this._map) this._map.removeLayer(this._deviationLine);
    this._deviationLine = null;
  }

  /** Uitgevoerde geplande boringen tonen of verbergen. */
  _toggleUitgevoerd() {
    this._showDone = !this._showDone;
    try { localStorage.setItem('mv-kaart-gepland-uitgevoerd', this._showDone ? 'aan' : 'uit'); } catch (_) { /* privévenster */ }
    if (this._map) { this._map.closePopup(); this._rebuildPlanned(); }
  }

  /** Geplande boringen tonen of verbergen op deze kaart. */
  _toggleGepland() {
    this._showPlanned = !this._showPlanned;
    try { localStorage.setItem('mv-kaart-gepland', this._showPlanned ? 'aan' : 'uit'); } catch (_) { /* privévenster */ }
    if (!this._map || !this._plannedLayer) return;
    if (this._showPlanned) this._plannedLayer.addTo(this._map);
    else { this._map.closePopup(); this._map.removeLayer(this._plannedLayer); }
  }

  /**
   * 'Verplaatsen' bij een gepland punt. Heeft het bedrijf Vooronderzoek, dan gebeurt dat daar
   * (samen met de deellocaties en het monsterplan); anders hier op de kaart.
   */
  async _verplaatsGepland(p) {
    if (!this.plannerAvailable) { this._startMovePoint(p.id); return; }
    this._map.closePopup();
    const ok = await confirmDialog({
      title: 'Verplaatsen in Vooronderzoek',
      message: 'Geplande boringen kun je alleen verplaatsen vanuit de pagina Vooronderzoek.',
      confirmLabel: 'Naar Vooronderzoek',
    });
    if (ok) this.dispatchEvent(new CustomEvent('open-vooronderzoek', { detail: { pointId: p.id }, bubbles: true, composed: true }));
  }

  _startMovePoint(id) {
    this._map.closePopup();
    this._movingPointId = id;
    this.renderRoot.querySelector('#map')?.classList.add('placing');
  }

  _cancelMovePoint() {
    this._movingPointId = null;
    this.renderRoot.querySelector('#map')?.classList.remove('placing');
  }

  _onMovePointClick(e) {
    if (!this._movingPointId) return;
    const id = this._movingPointId;
    this._cancelMovePoint();
    this.dispatchEvent(new CustomEvent('planned-point-update', {
      detail: { id, data: { x: e.latlng.lng, y: e.latlng.lat } }, bubbles: true, composed: true,
    }));
  }

  _flyToPlanned(id) {
    const m = this._plannedMarkers.get(id);
    if (!m) return;
    this._map.flyTo(m.getLatLng(), Math.max(this._map.getZoom(), 18), { duration: 0.7 });
    m.openPopup();
  }

  // ── Tekeningen ──────────────────────────────────────────────────────────────
  /**
   * Lagen op de kaart in lijn brengen met `mapLayers`. Het bestand van een laag wordt één keer
   * opgehaald (het staat achter auth, dus als blob) en daarna alleen nog aan/uit gezet.
   */
  async _syncLayers() {
    if (!this._map) return;
    const want = new Map((this.mapLayers || []).map(l => [l.id, l]));
    for (const [id, obj] of this._layerObjs) {
      const l = want.get(id);
      if (!l || !l.visible) { if (this._map.hasLayer(obj)) this._map.removeLayer(obj); }
      if (!l) { if (obj._mvUrl) URL.revokeObjectURL(obj._mvUrl); this._layerObjs.delete(id); }
    }
    for (const l of want.values()) {
      const obj = this._layerObjs.get(l.id);
      if (obj) {
        if (l.visible && !this._map.hasLayer(obj)) obj.addTo(this._map);
        if (l.kind === 'image' && l.georef) obj.setCorners(l.georef);
        applyOpacity(obj, l, layerOpacity(l));
        continue;
      }
      if (!l.visible || this._layerLoading.has(l.id)) continue;
      this._layerLoading.add(l.id);
      try {
        const blob = await api.fetchMapLayerBlob(l.id);
        let layer;
        if (l.kind === 'image') {
          const url = URL.createObjectURL(blob);
          layer = new RotatedImageOverlay(url, l.georef, { opacity: l.georef?.opacity ?? 0.85, pane: 'mvDrawings' });
          layer._mvUrl = url;
        } else {
          layer = vectorLayer(JSON.parse(await blob.text()), { ...(l.style || {}), opacity: layerOpacity(l) });
        }
        this._layerObjs.set(l.id, layer);
        const now = (this.mapLayers || []).find(x => x.id === l.id);
        if (now?.visible && this._map) layer.addTo(this._map);
      } catch (e) {
        console.warn('[project-map] tekening laden mislukt:', l.name, e.message);
      } finally {
        this._layerLoading.delete(l.id);
      }
    }
  }

  _toggleLayer(l) {
    this.dispatchEvent(new CustomEvent('map-layer-update', { detail: { id: l.id, data: { visible: !l.visible } }, bubbles: true, composed: true }));
  }

  _zoomToLayer(l) {
    const obj = this._layerObjs.get(l.id);
    if (obj && this._map.hasLayer(obj)) {
      const b = obj.getBounds();
      if (b.isValid()) this._map.fitBounds(b, { padding: [30, 30] });
    }
  }

  // ── Locatie plaatsen op de kaart (voor een boring zonder coördinaten) ────────
  _startPlacing(guid) {
    if (this._dlMode) this._cancelDrawing();
    this._placingGuid = guid;
    if (!this._map) return;
    this._map.closePopup();
    // Bestaande positie dimmen zolang we een nieuwe plek kiezen (oud vs. nieuw blijft zichtbaar).
    const em = this._markers.get(guid);
    if (em) em.setOpacity(0.35);
    const c = this.renderRoot.querySelector('#map');
    if (c) c.classList.add('placing');
    if (!this._onMapClickPlace) this._onMapClickPlace = (e) => this._onPlaceClick(e);
    this._map.on('click', this._onMapClickPlace);
  }

  _cancelPlacing() {
    const guid = this._placingGuid;
    this._placingGuid = null;
    if (this._map && this._onMapClickPlace) this._map.off('click', this._onMapClickPlace);
    if (this._placeMarker) { this._map.removeLayer(this._placeMarker); this._placeMarker = null; }
    const em = this._markers.get(guid);
    if (em) em.setOpacity(1); // gedimde bestaande marker weer volledig tonen
    const c = this.renderRoot?.querySelector('#map');
    if (c) c.classList.remove('placing');
  }

  _onPlaceClick(e) {
    if (!this._placingGuid) return;
    const b = (this.borings || []).find(x => x.guid === this._placingGuid);
    if (!b) return;
    const latlng = e.latlng;
    if (this._placeMarker) this._placeMarker.setLatLng(latlng);
    else this._placeMarker = L.marker(latlng, { icon: this._icon(b, true) }).addTo(this._map);
    this._placeMarker.bindPopup(this._placePopupNode(b, latlng), { offset: [0, -8], minWidth: 220, className: 'mv-map-popup', closeButton: true, autoPan: true });
    this._placeMarker.openPopup();
  }

  _placePopupNode(b, latlng) {
    const wrap = document.createElement('div');
    const sys = b.coordSys || 'WGS84';
    const proj = convertFromWgs84(latlng.lng, latlng.lat, sys) || { x: latlng.lng, y: latlng.lat };
    const isDeg = sys === 'WGS84';
    const fmt = isDeg
      ? (n) => Number(n).toLocaleString('nl-NL', { minimumFractionDigits: 6, maximumFractionDigits: 6 })
      : (n) => String(Math.round(n));
    // Bestaande boring (heeft al coördinaten) → 'wijzigen', anders 'toevoegen'.
    const isMove = b.lat != null && b.lng != null;
    wrap.innerHTML =
      `<div style="font-weight:700;font-size:13px;margin-bottom:6px;">${isMove ? 'Locatie wijzigen van' : 'Coördinaat toevoegen aan'} boring ${esc(b.name)}</div>
       <div style="font:600 11px/1.4 ui-monospace,SFMono-Regular,Menlo,monospace;color:#555;margin-bottom:10px;">${fmt(proj.x)}<br>${fmt(proj.y)}</div>`;
    const btn = document.createElement('button');
    btn.textContent = isMove ? 'Nieuwe locatie opslaan' : 'Coördinaat toevoegen';
    btn.style.cssText = 'width:100%;background:var(--mv-primary,#2e9e5b);color:#fff;border:none;border-radius:7px;padding:8px 10px;font-weight:700;font-size:12px;cursor:pointer;';
    btn.addEventListener('click', (ev) => { ev.stopPropagation(); this._confirmPlace(b.guid, latlng); });
    wrap.appendChild(btn);
    const cancel = document.createElement('div');
    cancel.textContent = 'Annuleren';
    cancel.style.cssText = 'text-align:center;color:#9096a0;font-size:11px;margin-top:7px;cursor:pointer;';
    cancel.addEventListener('click', (ev) => { ev.stopPropagation(); this._cancelPlacing(); });
    wrap.appendChild(cancel);
    return wrap;
  }

  _confirmPlace(guid, latlng) {
    // Boring slaat x=lengtegraad, y=breedtegraad op (WGS84) — net als elders in de app.
    this.dispatchEvent(new CustomEvent('set-boring-location', {
      detail: { guid, x: latlng.lng, y: latlng.lat }, bubbles: true, composed: true,
    }));
    this._cancelPlacing();
  }

  _popupNode(b) {
    const openBoring = () => this.dispatchEvent(new CustomEvent('open-boring', { detail: { guid: b.guid }, bubbles: true, composed: true }));

    // Hele popup klikbaar → boring openen (grote klikzone, geen zwaar groen blok).
    const wrap = document.createElement('div');
    wrap.style.cssText = 'position:relative;min-width:210px;font:400 12px/1.4 system-ui,-apple-system,sans-serif;color:#1a1a1a;cursor:pointer;';
    wrap.addEventListener('click', openBoring);

    // Mini-boorprofiel: laagkleuren proportioneel op diepte (het "sieraad" van het kaartje).
    // Flex normaliseert de dikte-verhoudingen vanzelf, dus geen totaal-diepte nodig. Elk
    // segment krijgt een subtiele hover-tekst (native title) die de laag toelicht.
    const segs = (b.profile || []).map(s => {
      const tip = esc(`${s.from}–${s.to} cm${s.label ? ` · ${s.label}` : ''}`);
      return `<div data-tip="${tip}" style="flex:${Math.max(0.001, (s.to - s.from))};background:${s.color};"></div>`;
    }).join('');
    const bar = (b.profile && b.profile.length)
      ? `<div class="mv-profilebar" style="width:11px;align-self:stretch;min-height:66px;border-radius:3px;overflow:hidden;display:flex;flex-direction:column;border:1px solid rgba(0,0,0,0.08);flex-shrink:0;">${segs}</div>`
      : '';

    // Feiten in twee kolommen (grijs label links, waarde links uitgelijnd) — scannen i.p.v. lezen.
    // De actuele boring, niet die van toen de marker getekend werd: bij alleen een andere
    // deellocatie worden de markers niet opnieuw gemaakt (zelfde plek), dus `b` is dan oud.
    const dlIds = this._sublocationIdsOf((this.borings || []).find((x) => x.guid === b.guid) || b);
    const dl = this._sublocations.filter((s) => dlIds.includes(s.id)).map((s) => esc(s.name)).join(' + ');
    // Het geplande punt dat deze boring uitvoert (expliciet gekoppeld of op naam), zoals de
    // backend het bepaalt (status/boringId van de geplande punten).
    const gepland = b.id ? (this.plannedPoints || []).find((q) => q.boringId === b.id) : null;
    const rows = [
      gepland ? ['Gepland punt', `<span style="color:#2e9e5b;">${esc(gepland.name)}</span>`] : null,
      dl ? ['Deellocatie', `<span style="color:#c2410c;">${dl}</span>`] : null,
      ['Diepte', b.depth ? `${b.depth} cm` : '–'],
      ['Lagen', String(b.lagen ?? 0)],
      b.peilbuisName ? ['Peilbuis', esc(b.peilbuisName)] : null,
      b.heightText ? ['Maaiveld', esc(b.heightText)] : null,
    ].filter(Boolean).map(([k, v]) => `<div style="color:#8a8f98;">${k}</div><div style="font-weight:600;">${v}</div>`).join('');

    wrap.innerHTML =
      `<div style="display:flex;align-items:baseline;gap:7px;margin-bottom:9px;">
         <span style="font-weight:700;font-size:13px;">${esc(b.name)}</span>
         <span style="color:#9096a0;font-size:11px;">definitieve boring</span>
       </div>
       <div style="display:flex;gap:18px;align-items:stretch;">${bar}
         <div style="flex:1;display:grid;grid-template-columns:72px 1fr;gap:5px 10px;align-content:start;">${rows}</div>
       </div>`;

    // Voettekst: coördinaten in monospace + kopieerknop, en 'Open boring' als tekstlink.
    const foot = document.createElement('div');
    foot.style.cssText = 'display:flex;align-items:center;justify-content:space-between;gap:10px;margin-top:11px;padding-top:9px;border-top:1px solid #ececec;';

    const coordWrap = document.createElement('div');
    if (b.coordX != null && b.coordY != null) {
      // Graden (lat/long) hebben decimalen nodig (afronden → nutteloos '5'/'52'); geprojecteerde
      // meters (RD/UTM) tonen we afgerond. De kopie krijgt altijd de volle precisie.
      const isDeg = b.coordSys === 'WGS84';
      const fmt = isDeg
        ? (n) => n.toLocaleString('nl-NL', { minimumFractionDigits: 6, maximumFractionDigits: 6 })
        : (n) => String(Math.round(n));
      const copyVal = isDeg ? (n) => n.toFixed(7) : (n) => String(Math.round(n));
      coordWrap.style.cssText = 'display:flex;align-items:center;gap:7px;';
      coordWrap.innerHTML = `<span style="font:600 11px/1.35 ui-monospace,SFMono-Regular,Menlo,monospace;color:#555;">${fmt(b.coordX)}<br>${fmt(b.coordY)}</span>`;
      const copy = document.createElement('button');
      copy.title = 'Coördinaten kopiëren';
      copy.style.cssText = 'border:none;background:none;cursor:pointer;color:#9096a0;padding:2px;display:inline-flex;align-items:center;';
      copy.innerHTML = lucideIcon(Copy, 13);
      copy.addEventListener('click', (e) => {
        e.stopPropagation();
        navigator.clipboard?.writeText(`${copyVal(b.coordX)} ${copyVal(b.coordY)}`);
        copy.innerHTML = lucideIcon(Check, 13); copy.style.color = 'var(--mv-primary,#2e9e5b)';
        setTimeout(() => { copy.innerHTML = lucideIcon(Copy, 13); copy.style.color = '#9096a0'; }, 1200);
      });
      coordWrap.appendChild(copy);
    }
    foot.appendChild(coordWrap);

    const link = document.createElement('span');
    link.style.cssText = 'color:var(--mv-primary,#2e9e5b);font-weight:700;font-size:12px;white-space:nowrap;';
    link.textContent = 'Open boring →';
    foot.appendChild(link);

    wrap.appendChild(foot);

    // 'Locatie wijzigen' → dezelfde klik-op-de-kaart-flow als bij een boring zonder coördinaten.
    const move = document.createElement('div');
    move.style.cssText = 'margin-top:8px;display:inline-flex;align-items:center;gap:6px;color:#5b6672;font-weight:600;font-size:12px;cursor:pointer;';
    move.innerHTML = `${lucideIcon(MapPin, 13)}<span>Locatie wijzigen</span>`;
    move.addEventListener('mouseenter', () => { move.style.color = 'var(--mv-primary,#2e9e5b)'; });
    move.addEventListener('mouseleave', () => { move.style.color = '#5b6672'; });
    move.addEventListener('click', (e) => { e.stopPropagation(); this._startPlacing(b.guid); });
    wrap.appendChild(move);

    // Snelle laag-tooltip die de cursor volgt — gedeeld met de boringen-/afwerkingbalk.
    const barEl = wrap.querySelector('.mv-profilebar');
    if (barEl) {
      barEl.addEventListener('mousemove', tipMove);
      barEl.addEventListener('mouseleave', tipHide);
    }

    return wrap;
  }

  /** Selecteer een boring (vanuit de lijst): open de popup en vlieg er eventueel naartoe. */
  _select(guid, fly) {
    const m = this._markers.get(guid);
    if (!m) return;
    if (fly) this._map.flyTo(m.getLatLng(), Math.max(this._map.getZoom(), 18), { duration: 0.7 });
    m.openPopup(); // → popupopen regelt de selectie-styling
  }

  /** Popup ging open (marker-klik óf lijst-klik) → markeer als geselecteerd. */
  _onPopupOpen(guid) {
    const prev = this._selected;
    this._selected = guid;
    for (const g of [prev, guid]) {
      const m = this._markers.get(g);
      if (m && this._byGuid.get(g)) m.setIcon(this._icon(this._byGuid.get(g), g === guid));
    }
    const m = this._markers.get(guid);
    if (m) m.setZIndexOffset(1000);
    this.requestUpdate();
  }

  /** Popup ging dicht → selectie opheffen. */
  _onPopupClose(guid) {
    if (this._selected !== guid) return;
    this._selected = null;
    const m = this._markers.get(guid);
    if (m && this._byGuid.get(guid)) m.setIcon(this._icon(this._byGuid.get(guid), false));
    this.requestUpdate();
  }

  /** 'Alles tonen': popup sluiten + terug naar de volledige uitsnede. */
  _showAll() { if (this._map) this._map.closePopup(); this._fitAll(); }

  _addBase(kind) { for (const l of this._tiles[kind]) l.addTo(this._map); }
  _removeBase(kind) { for (const l of this._tiles[kind]) this._map.removeLayer(l); }

  _setBase(kind) {
    if (kind === this._base) return;
    if (this._map) { this._removeBase(this._base); this._addBase(kind); }
    this._base = kind;
  }

  _matches(b) {
    const q = this._query.trim().toLowerCase();
    return !q || (b.name || '').toLowerCase().includes(q);
  }

  /**
   * Legenda: het verschil tussen een definitieve boring (in het veld vastgelegd, tabel
   * `borings`) en een gepland punt (tabel `planned_points`), en of dat geplande punt al is
   * uitgevoerd. Geplande soorten alleen als ze in dit project voorkomen.
   */
  /** Het paneel 'Tekeningen op de kaart' (gedeeld met Vooronderzoek, zie utils/tekeningen-paneel.js). */
  _renderTekeningenPaneel(layers) {
    const meld = (type, detail) => this.dispatchEvent(new CustomEvent(type, { detail, bubbles: true, composed: true }));
    return renderTekeningenPaneel({
      lagen: layers,
      onToggle: (l) => this._toggleLayer(l),
      onOpacityInput: (l, o) => applyOpacity(this._layerObjs.get(l.id), l, o),
      onOpacityChange: (l, o) => meld('map-layer-update', { id: l.id, data: opacityPatch(l, o) }),
      onRealign: (l) => { this._layersOpen = false; meld('map-layer-realign', { layer: l }); },
      onDelete: (l) => meld('map-layer-delete', { id: l.id, name: l.name }),
      onZoom: (l) => this._zoomToLayer(l),
      onAdd: () => { this._layersOpen = false; meld('open-import'); },
      onClose: () => { this._layersOpen = false; },
    });
  }

  _renderLegend() {
    if (this._legendaDicht === undefined) this._legendaDicht = legendaIngeklapt();
    return renderKaartLegenda({
      borings: this.borings, plannedPoints: this._showPlanned ? this.plannedPoints : [],
      ingeklapt: this._legendaDicht,
      onToggle: () => { this._legendaDicht = !this._legendaDicht; bewaarLegendaIngeklapt(this._legendaDicht); },
    });
  }

  render() {
    const located = this._located.filter(b => this._matches(b));
    const noGps = this._noGps.filter(b => this._matches(b));
    const total = (this.borings || []).length;
    const planned = (this.plannedPoints || []).filter(p => this._matches(p));
    const openPlanned = planned.filter(p => p.status !== 'done');
    const layers = this.mapLayers || [];

    return html`
      <div class="wrap">
        <div class="side">
          <div class="search">
            <div class="search-box">
              ${unsafeSVG(lucideIcon(Search, 14))}
              <input type="text" placeholder="Zoek boring…" .value=${this._query}
                @input=${e => this._query = e.target.value}>
            </div>
          </div>
          <div class="list">
            ${this._sublocations.length ? html`
              <div class="sec-label">Deellocaties · ${this._sublocations.length}</div>
              ${this._sublocations.map(s => html`
                <div class="item ${s.id === this._dlSelectedId ? 'dl-active' : ''}"
                     @click=${() => (this.compact ? (s.geometry?.length >= 3 && this._fitSublocation(s)) : this._selectSublocation(s))}>
                  <span class="item-ico" style="color:#c2410c">${unsafeSVG(lucideIcon(Pentagon, 15))}</span>
                  <div class="item-text">
                    <div class="item-name">${s.name}</div>
                    <div class="item-sub">${(n => n === 1 ? '1 boring' : `${n} boringen`)(this._boringsIn(s).length)}${s.description ? ` · ${s.description}` : ''}</div>
                  </div>
                </div>`)}
              <div class="sec-label">Boringen</div>` : ''}
            ${located.map(b => {
              const dlIds = this._sublocationIdsOf(b);
              const dl = this._sublocations.filter((s) => dlIds.includes(s.id)).map((s) => s.name).join(' + ');
              return html`
              <div class="item ${b.guid === this._selected ? 'active' : ''}" @click=${() => { if (this._dlSelectedId) this._closeSublocation(); this._select(b.guid, true); }}>
                <span class="item-ico">${unsafeSVG(lucideIcon(b.peilbuisName ? TestTube : MapPin, 15))}</span>
                <div class="item-text">
                  <div class="item-name">${b.name}</div>
                  <div class="item-sub">boring · ${b.depth || 0} cm${dl ? html` · <span class="item-dl">${dl}</span>` : ''}</div>
                </div>
              </div>`;
            })}
            ${located.length === 0 && !noGps.length && !planned.length ? html`<div class="sec-label" style="padding:12px 8px;">Geen boringen gevonden.</div>` : ''}
            ${noGps.length ? html`
              <div class="sec-label">Zonder locatie · ${noGps.length}</div>
              ${noGps.map(b => html`
                <div class="item nogps ${b.guid === this._placingGuid ? 'placing' : ''}">
                  <span class="item-ico">${unsafeSVG(lucideIcon(MapPin, 15))}</span>
                  <div class="item-text">
                    <div class="item-name">${b.name}</div>
                    <a class="add-coord" @click=${(e) => { e.stopPropagation(); this._startPlacing(b.guid); }}>
                      ${b.guid === this._placingGuid ? 'Klik op de kaart…' : 'Coördinaten toevoegen'}
                    </a>
                  </div>
                </div>`)}` : ''}
            ${openPlanned.length && this._showPlanned ? html`
              <div class="sec-label">Nog te boren · ${openPlanned.length} van ${(this.plannedPoints || []).length}</div>
              ${openPlanned.map(p => html`
                <div class="item" @click=${() => { if (this._dlSelectedId) this._closeSublocation(); this._flyToPlanned(p.id); }}>
                  <span class="item-ico planned ${p.kind}"></span>
                  <div class="item-text">
                    <div class="item-name">${p.name}</div>
                    <div class="item-sub">gepland${p.plannedDepthCm ? ` · ${p.plannedDepthCm} cm` : ''}</div>
                  </div>
                </div>`)}` : ''}
          </div>
        </div>

        <div class="map-area">
          <div id="map"></div>
          ${this._dlSelected && !this.compact ? html`
            <!-- Hetzelfde venster als achter 'Beheren' bij het samenstellen van een labopdracht. -->
            <sublocation-editor class="dl-panel" .projectId=${this.projectId} .sublocation=${this._dlSelected}
                                .sublocations=${this._sublocations} .borings=${this.borings || []}
                                @sublocation-overrides=${(e) => { this._dlOverrides = { ...this._dlOverrides, ...e.detail.overrides }; }}
                                @sublocation-deleted=${() => { this._dlSelectedId = null; }}
                                @close=${() => this._closeSublocation()}></sublocation-editor>` : ''}
          ${this._placingGuid ? (() => {
            const pb = (this.borings || []).find(x => x.guid === this._placingGuid);
            return html`<div class="place-hint">
              ${unsafeSVG(lucideIcon(MapPin, 15))}
              <span>Klik op de kaart om de locatie van <b>${pb ? pb.name : ''}</b> te plaatsen.</span>
              <button @click=${() => this._cancelPlacing()}>Annuleren</button>
            </div>`;
          })() : ''}
          ${this._dlMode === 'drawing' ? html`<div class="place-hint dl-bar">
              ${unsafeSVG(lucideIcon(Pentagon, 15))}
              <span>${this._dlPoints < 3
                ? html`Klik de hoekpunten van de deellocatie aan${this._dlPoints ? html` (<b>${this._dlPoints}</b> gezet)` : ''}.`
                : html`<b>${this._dlPoints}</b> punten. Klik op het eerste punt of dubbelklik om af te sluiten.`}</span>
              <button ?disabled=${!this._dlPoints} @click=${() => this._drawer.undo()}>Punt terug</button>
              <button class="primary" ?disabled=${this._dlPoints < 3} @click=${() => this._drawer.finish()}>Afsluiten</button>
              <button @click=${() => this._cancelDrawing()}>Annuleren</button>
            </div>` : ''}
          ${this._dlMode === 'naming' ? html`<div class="place-hint dl-bar">
              ${unsafeSVG(lucideIcon(Pentagon, 15))}
              <span>Naam:</span>
              <input .value=${this._dlName} maxlength="60" aria-label="Naam van de deellocatie"
                @input=${e => { this._dlName = e.target.value; }}
                @keydown=${e => { if (e.key === 'Enter') this._saveDrawing(); }}>
              <button class="primary" ?disabled=${this._dlBusy} @click=${() => this._saveDrawing()}>${this._dlBusy ? 'Opslaan…' : 'Opslaan'}</button>
              <button ?disabled=${this._dlBusy} @click=${() => this._cancelDrawing()}>Annuleren</button>
              ${this._dlError ? html`<span class="dl-error">${this._dlError}</span>` : ''}
            </div>` : ''}
          ${this._movingPointId ? html`<div class="place-hint">
              ${unsafeSVG(lucideIcon(MapPin, 15))}
              <span>Klik op de kaart voor de nieuwe plek van <b>${(this.plannedPoints || []).find(p => p.id === this._movingPointId)?.name || ''}</b>.</span>
              <button @click=${() => this._cancelMovePoint()}>Annuleren</button>
            </div>` : ''}
          <div class="overlay-tr">
            ${this.projectId && !this._dlMode ? html`
              <button class="btn-plain" title="Teken een deellocatie op de kaart" @click=${() => this._startDrawing()}>
                ${unsafeSVG(lucideIcon(Pentagon, 13))} Deellocatie</button>` : ''}
            <div class="tekeningmenu">
              <button class="btn-plain ${this._layersOpen ? 'on' : ''}" @click=${() => { this._layersOpen = !this._layersOpen; }}>
                ${unsafeSVG(lucideIcon(ImageIcon, 13))} Tekeningen${layers.length ? html`<span class="telling">${layers.length}</span>` : ''}</button>
              ${this._layersOpen ? this._renderTekeningenPaneel(layers) : ''}
            </div>
            ${(this.plannedPoints || []).length ? html`
              <button class="btn-plain ${this._showPlanned ? 'on' : ''}" title=${this._showPlanned ? 'Geplande boringen verbergen' : 'Geplande boringen tonen'}
                @click=${() => this._toggleGepland()}>
                ${unsafeSVG(lucideIcon(this._showPlanned ? Eye : EyeOff, 13))} Gepland</button>` : ''}
            ${this._showPlanned && (this.plannedPoints || []).some((p) => p.status === 'done') ? html`
              <button class="btn-plain ${this._showDone ? 'on' : ''}"
                data-tip="Toont geplande boringen die al zijn vervangen door een echte boring. Klik er een aan om te zien hoe ver de boring van de geplande plek staat."
                @mousemove=${tipMove} @mouseleave=${tipHide}
                @click=${() => this._toggleUitgevoerd()}>
                ${unsafeSVG(lucideIcon(this._showDone ? Eye : EyeOff, 13))} Uitgevoerd</button>` : ''}
            <button class="btn-plain" @click=${() => this._showAll()}>${unsafeSVG(lucideIcon(Layers, 13))} Alles tonen</button>
            <div class="seg">
              <button class=${this._base === 'map' ? 'on' : ''} @click=${() => this._setBase('map')}>Kaart</button>
              <button class=${this._base === 'aerial' ? 'on' : ''} @click=${() => this._setBase('aerial')}>Luchtfoto</button>
            </div>
          </div>
          ${this._renderLegend()}
          <div class="footer">${this._located.length} van ${total} op de kaart${(this.plannedPoints || []).length ? ` · ${(this.plannedPoints || []).length - openPlanned.length}/${(this.plannedPoints || []).length} gepland uitgevoerd` : ''}</div>
          ${this._located.length === 0 && !(this.plannedPoints || []).length && !layers.length ? html`<div class="empty">Nog geen boringen met coördinaten.<br>Leg locaties vast in de veld-app, of open een meetstaat of tekening met de boorlocaties.</div>` : ''}
        </div>
      </div>`;
  }
}

customElements.define('project-map', ProjectMap);
